import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ArrowRight, ChevronDown } from "lucide-react";
import { Reveal, GoldRule } from "@/components/Shared";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { InlineConcierge, ProofStrip, CompactPractitioner, OfferLine } from "@/components/Landing";
import { ConciergeButton } from "@/components/Concierge";
import { CLINIC, getTeamMember } from "@/data/content";
import { HAIR_SECTION_NAV, HAIR_MENU, HAIR_BOOK_PATH, HAIR_HERO, HAIR_LANDING, HAIR_CREDENTIAL, PRACTICE_STANDARDS, PATHWAY } from "@/data/hair";
import { trackEvent } from "@/lib/analytics";

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export const HairWordmark = ({ className = "", testId = "hair-wordmark" }) => (
  <Link to="/hair" aria-label="Lumière Hair Clinic — section home" data-testid={testId} className={`inline-flex flex-col items-start leading-none ${className}`}>
    <span className="font-serif font-light text-gold text-[22px] tracking-[0.32em]">LUMIÈRE</span>
    <span className="font-serif text-gold text-[10px] tracking-[0.42em] uppercase mt-1.5 ml-[2px]">Hair Clinic</span>
  </Link>
);

export const HairBookButton = ({ label = "Book an assessment", className = "btn-hair-gold px-7 py-3.5 text-sm", source = "cta", testId = "hair-book-button" }) => {
  const { pathname } = useLocation();
  return (
    <Link to={HAIR_BOOK_PATH} onClick={() => trackEvent("hair_booking_started", { page: pathname, source })} data-testid={testId} className={className}>
      {label}
    </Link>
  );
};

const Dropdown = ({ open, column, testPrefix }) => (
  <AnimatePresence>
    {open && (
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.22 }} className="absolute top-full left-1/2 -translate-x-1/2 pt-5">
        <div className="bg-hair-panel border border-gold/25 shadow-[0_12px_40px_rgba(0,0,0,0.45)] py-5 px-7 w-[300px]" data-testid={`${testPrefix}-dropdown`}>
          {column.links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`${testPrefix}-${slugify(l.label)}`} className="block py-2.5 font-serif text-[16px] text-warm-white/85 hover:text-gold transition-colors duration-200 border-b border-gold/10 last:border-0">
              {l.label}
            </Link>
          ))}
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

export const HairHeader = () => {
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); setMenu(null); }, [pathname]);
  const linkClass = (active) => `text-[12px] tracking-[0.16em] uppercase transition-colors duration-300 ${active ? "text-gold" : "text-warm-white/80 hover:text-gold"}`;
  const flat = [
    { to: "/hair/dr-kristy-truong", label: "Dr Truong" },
    { to: "/hair#faq", label: "FAQ" },
    { to: "/hair#clinic", label: "The clinic" },
  ];

  return (
    <header className="fixed top-0 inset-x-0 z-50" data-testid="hair-header">
      <div className="bg-hair-dark/95 backdrop-blur-md border-b border-gold/20">
      <div className="max-w-[1680px] mx-auto px-6 md:px-10 2xl:px-16 flex items-center justify-between h-[72px]">
        <HairWordmark />
        <nav className="hidden xl:flex items-center gap-7" aria-label="Lumière Hair Clinic">
          {[HAIR_MENU.hairLoss, HAIR_MENU.treatments].map((col) => {
            const key = slugify(col.label);
            return (
              <div key={key} className="relative" onMouseEnter={() => setMenu(key)} onMouseLeave={() => setMenu(null)}>
                <NavLink to={col.to} data-testid={`hair-nav-${key}`} className={`${linkClass(pathname.startsWith(col.to) || (key === "treatments" && pathname.startsWith("/hair/transplantation")))} inline-flex items-center gap-1`}>
                  {col.label} <ChevronDown className="w-3 h-3" strokeWidth={1.5} />
                </NavLink>
                <Dropdown open={menu === key} column={col} testPrefix={`hair-nav-${key}`} />
              </div>
            );
          })}
          {flat.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`hair-nav-${slugify(l.label)}`} className={linkClass(l.to === pathname)}>
              {l.label}
            </Link>
          ))}
          <a href={`tel:${CLINIC.phoneTel}`} data-testid="hair-nav-phone" className="hidden 2xl:inline text-[12px] tracking-[0.14em] text-warm-white/70 hover:text-gold transition-colors duration-300">
            {CLINIC.phone}
          </a>
          <HairBookButton className="btn-hair-gold px-6 py-3 text-[12px] min-h-[44px] whitespace-nowrap" source="header" testId="hair-nav-book" />
          <Link to="/" data-testid="hair-nav-main-site" className="hidden [@media(min-width:1700px)]:inline-flex items-center gap-1 text-[11px] tracking-[0.14em] uppercase text-warm-white/50 hover:text-gold transition-colors duration-300">
            Lumière Aesthetics <ArrowUpRight className="w-3 h-3" strokeWidth={1.5} />
          </Link>
        </nav>
        <div className="flex items-center gap-3 xl:hidden">
          <HairBookButton className="btn-hair-gold hidden sm:inline-flex px-5 py-2.5 text-[11px] min-h-[40px] whitespace-nowrap" source="header" testId="hair-nav-book-compact" />
          <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} aria-label={open ? "Close menu" : "Open menu"} data-testid="hair-menu-toggle" className="text-warm-white p-2 -mr-2">
            {open ? <X className="w-6 h-6" strokeWidth={1.25} /> : <Menu className="w-6 h-6" strokeWidth={1.25} />}
          </button>
        </div>
      </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} className="xl:hidden fixed inset-x-0 top-[72px] h-[calc(100dvh-72px)] bg-hair-dark px-8 pt-4 pb-28 overflow-y-auto" data-testid="hair-mobile-menu">
            <nav className="flex flex-col" aria-label="Lumière Hair Clinic mobile">
              {[HAIR_MENU.hairLoss, HAIR_MENU.treatments].map((col) => {
                const key = slugify(col.label);
                const isOpen = expanded === key;
                return (
                  <div key={key} className="border-b border-gold/15">
                    <div className="flex items-center justify-between">
                      <Link to={col.to} data-testid={`hair-mobile-nav-${key}`} className="font-serif font-light text-warm-white text-3xl py-3.5 block">{col.label}</Link>
                      <button type="button" onClick={() => setExpanded(isOpen ? null : key)} aria-expanded={isOpen} aria-label={`Expand ${col.label}`} data-testid={`hair-mobile-toggle-${key}`} className="text-gold p-2">
                        <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} strokeWidth={1.25} />
                      </button>
                    </div>
                    {isOpen && (
                      <div className="pb-4">
                        {col.links.map((l) => (
                          <Link key={l.to} to={l.to} data-testid={`hair-mobile-${key}-${slugify(l.label)}`} className="block py-2 font-serif text-lg text-warm-white/75">{l.label}</Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {[...flat, { to: "/hair/journal", label: "Journal" }].map((l) => (
                <Link key={l.to} to={l.to} data-testid={`hair-mobile-nav-${slugify(l.label)}`} className="font-serif font-light text-warm-white text-3xl py-3.5 border-b border-gold/15">
                  {l.label}
                </Link>
              ))}
              <HairBookButton className="btn-hair-gold w-full py-4 text-sm mt-8" source="mobile-menu" testId="hair-mobile-book" />
              <a href={`tel:${CLINIC.phoneTel}`} data-testid="hair-mobile-phone" className="mt-6 text-center text-[13px] tracking-[0.14em] text-warm-white/75">Call {CLINIC.phone}</a>
              <Link to="/" data-testid="hair-mobile-main-site" className="inline-flex items-center justify-center gap-1.5 mt-6 text-[11px] tracking-[0.16em] uppercase text-warm-white/55">
                Lumière Aesthetics <ArrowUpRight className="w-3 h-3" strokeWidth={1.5} />
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export const HairFooter = () => (
  <footer className="bg-hair-dark border-t border-gold/20 text-warm-white/75" data-testid="hair-footer">
    <div className="shell py-16 lg:py-20 grid gap-12 lg:grid-cols-3">
      <div>
        <HairWordmark testId="hair-footer-wordmark" />
        <p className="text-sm leading-relaxed mt-6 max-w-sm" data-testid="hair-footer-entity">
          Lumière Hair Clinic is the hair restoration section of Lumière Aesthetics — {CLINIC.legal} (ABN {CLINIC.abn}).
        </p>
        <p className="text-sm mt-4">{CLINIC.address}</p>
        <a href={`tel:${CLINIC.phoneTel}`} className="text-sm hover:text-gold transition-colors duration-300 inline-block mt-1" data-testid="hair-footer-phone">{CLINIC.phone}</a>
        <p className="text-[11px] tracking-[0.16em] uppercase text-gold mt-5" data-testid="hair-footer-doctor">Dr Kristy Truong · AHPRA MED0002351485</p>
      </div>
      <div>
        <p className="eyebrow eyebrow-dark mb-4">Lumière Hair Clinic</p>
        <ul className="space-y-2.5">
          {[...HAIR_SECTION_NAV, { to: "/hair/journal", label: "Journal" }, { to: HAIR_BOOK_PATH, label: "Book an assessment" }].map((l) => (
            <li key={l.to}><Link to={l.to} className="font-serif text-lg text-warm-white/85 hover:text-gold transition-colors duration-300" data-testid={`hair-footer-link-${slugify(l.label)}`}>{l.label}</Link></li>
          ))}
        </ul>
      </div>
      <div>
        <p className="eyebrow eyebrow-dark mb-4">The practice</p>
        <ul className="space-y-2.5">
          {[["Lumière Aesthetics", "/"], ["Pricing", "/pricing"], ["Privacy", "/privacy"], ["Terms", "/terms"], ["Complaints", "/complaints"], ["Booking & cancellation policy", "/booking-and-cancellation-policy"]].map(([label, to]) => (
            <li key={to}><Link to={to} className="text-sm text-warm-white/70 hover:text-gold transition-colors duration-300" data-testid={`hair-footer-main-${slugify(label)}`}>{label}</Link></li>
          ))}
        </ul>
        <p className="text-xs leading-relaxed text-warm-white/45 mt-8">
          Imagery may include models. Individual results vary. Any surgical or invasive procedure carries risks — before proceeding, seek a second opinion from an appropriately qualified health practitioner.
        </p>
      </div>
    </div>
    <div className="border-t border-gold/10">
      <div className="shell py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-[11px] tracking-[0.14em] uppercase text-warm-white/40">
        <span>© {new Date().getFullYear()} {CLINIC.legal} · Cottesloe, Western Australia</span>
        <Link to="/" className="inline-flex items-center gap-1 hover:text-gold transition-colors duration-300">Lumière Aesthetics <ArrowUpRight className="w-3 h-3" strokeWidth={1.5} /></Link>
      </div>
    </div>
  </footer>
);

export const HairBreadcrumb = ({ items, dark = false }) => (
  <nav aria-label="Breadcrumb" className={`${dark ? "bg-hair-dark" : "bg-warm-white"}`} data-testid="hair-breadcrumb">
    <ol className={`shell py-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] tracking-[0.14em] uppercase ${dark ? "text-warm-white/50" : "text-soft-grey"}`}>
      {items.map((item, i) => (
        <li key={item.to || item.name} className="flex items-center gap-2">
          {i > 0 && <span aria-hidden="true" className="text-gold/60">›</span>}
          {i < items.length - 1 && item.to ? (
            <Link to={item.to} className="hover:text-gold transition-colors duration-300">{item.name}</Link>
          ) : (
            <span className={dark ? "text-warm-white/85" : "text-ink"}>{item.name}</span>
          )}
        </li>
      ))}
    </ol>
  </nav>
);

export const HairBand = ({ children, dark = false, sand = false, narrow = false, testId, className = "" }) => (
  <section className={`${dark ? "hair-dark bg-hair-dark text-warm-white" : sand ? "bg-sand text-ink" : "bg-warm-white text-ink"} ${className}`} data-testid={testId}>
    <div className={`${narrow ? "max-w-4xl mx-auto px-8" : "shell"} py-16 lg:py-24`}>{children}</div>
  </section>
);

export const HairEyebrow = ({ children, className = "" }) => <p className={`eyebrow text-gold ${className}`}>{children}</p>;

export const HairHeading = ({ children, dark = false, className = "" }) => (
  <h2 className={`${dark ? "text-warm-white" : "text-ink"} ${className}`}>{children}</h2>
);

export const Bullets = ({ items, dark = false, testId }) => (
  <ul className="space-y-2.5" data-testid={testId}>
    {items.map((item, i) => (
      <li key={i} className={`flex gap-3 text-[15px] leading-relaxed ${dark ? "text-warm-white/85" : "text-ink/85"}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-gold mt-2.5 shrink-0" aria-hidden="true" />
        {item}
      </li>
    ))}
  </ul>
);

export const Paras = ({ items, dark = false, size = "text-[17px]" }) => (
  <div className="space-y-5">
    {items.map((p, i) => (
      <p key={i} className={`${size} leading-[1.8] ${dark ? "text-warm-white/85" : "text-ink/85"}`}>{p}</p>
    ))}
  </div>
);

export const HairHero = ({
  eyebrow, title, standfirst, image = HAIR_HERO.room, offer, member = getTeamMember("truong"), form = true, cta = true, tall = true, bullets, portrait = false, mobileImage = false, testId = "hair-hero", children,
}) => {
  const person = member && member.slug === "dr-kristy-truong" ? { ...member, credential: HAIR_CREDENTIAL } : member;
  const band = portrait || mobileImage;
  return (
  <section className={`hair-dark relative bg-hair-dark ${tall ? "lg:min-h-[92vh]" : "lg:min-h-[64vh]"} lg:grid lg:grid-cols-5`} data-testid={testId}>
    <div className={`${form ? `hidden lg:block absolute inset-y-0 left-0 ${portrait ? "w-3/4" : "w-3/5"}` : `${band ? "hidden lg:block" : ""} absolute inset-0`}`} aria-hidden="true">
      <img src={image.id} alt="" loading="eager" decoding="async" style={{ objectPosition: image.pos || "50% 40%" }} className="filmic absolute inset-0 w-full h-full object-cover" data-testid="hero-image" />
      {portrait ? (
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(35,33,30,0.97) 0%, rgba(35,33,30,0.88) 34%, rgba(35,33,30,0.35) 56%, rgba(35,33,30,0) 72%, rgba(35,33,30,0.55) 100%)" }} />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-r from-[rgba(35,33,30,0.98)] via-[rgba(35,33,30,0.82)] to-[rgba(35,33,30,0.34)]" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[rgba(35,33,30,0.94)] via-transparent to-[rgba(35,33,30,0.45)]" />
    </div>

    {band && (
      <div className="lg:hidden relative mt-[72px] aspect-[4/3] sm:aspect-[16/9] overflow-hidden" data-testid="hero-image-mobile">
        <img src={image.id} alt={image.alt || ""} loading="eager" decoding="async" style={{ objectPosition: image.mobilePos || image.pos || "50% 20%" }} className="filmic absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-hair-dark via-transparent to-[rgba(35,33,30,0.35)]" />
      </div>
    )}

    <div className={`relative z-10 ${form ? "lg:col-span-3" : "lg:col-span-5"} px-6 sm:px-10 lg:pl-[120px] lg:pr-16 ${portrait ? "pt-6 lg:pt-28 lg:pb-16 lg:justify-start" : `${band ? "pt-6" : "pt-[112px]"} lg:pt-32 lg:pb-32 lg:justify-center`} pb-10 flex flex-col`}>
      <div className="gold-frame inset-4 sm:inset-6 opacity-50 hidden lg:block" aria-hidden="true" />
      <Reveal y={16}>
        <p className="eyebrow eyebrow-dark">{eyebrow}</p>
        <h1 className={`text-warm-white max-w-2xl ${portrait ? "lg:text-[64px] 2xl:text-[76px]" : ""}`} data-testid="hero-heading">{title}</h1>
        {standfirst && <p className="standfirst text-warm-white/85 max-w-xl text-[19px] lg:text-[24px] !mt-5 lg:!mt-8" data-testid="hero-standfirst">{standfirst}</p>}
      </Reveal>
      <Reveal y={12} delay={0.1}>
        {person && <CompactPractitioner member={person} photo={!portrait} className={portrait ? "mt-7" : "mt-8"} />}
        {offer && <OfferLine text={offer} className="mt-6" />}
        {bullets ? (
          <ul className="mt-8 space-y-2.5" data-testid="hero-trust-bullets">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-[14px] leading-snug text-warm-white/85" data-testid={`hero-trust-${i}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-gold mt-[7px] shrink-0" aria-hidden="true" />
                {b}
              </li>
            ))}
          </ul>
        ) : (
          <ProofStrip variant="hair" className="mt-8" />
        )}
        {cta && (
          <div className="mt-8 flex flex-wrap items-center gap-4" data-testid="hero-cta-row">
            <HairBookButton source="hero" testId="hero-book-button" />
            {children}
          </div>
        )}
      </Reveal>
    </div>

    {form && (
      <div className={`relative z-10 lg:col-span-2 bg-hair-panel px-5 sm:px-10 lg:px-12 py-8 flex flex-col ${portrait ? "lg:pt-44 lg:pb-16 lg:justify-end" : "lg:py-32 lg:justify-center"}`}>
        <Reveal y={12} delay={0.15}>
          <InlineConcierge member={member} hair bookTo={HAIR_BOOK_PATH} />
        </Reveal>
      </div>
    )}
  </section>
  );
};

export const HairCard = ({ to, title, text, dark = false, testId, cta = "Read" }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`block p-7 h-full group transition-all duration-500 hover:-translate-y-1 ${dark ? "bg-hair-panel border border-gold/20 hover:border-gold/60" : "card-float"}`}
  >
    <h3 className={dark ? "text-warm-white" : "text-ink"}>{title}</h3>
    <p className={`text-sm mt-2 leading-relaxed ${dark ? "text-warm-white/70" : "text-ink/70"}`}>{text}</p>
    <span className={`inline-flex items-center gap-2 mt-4 text-[11px] tracking-[0.18em] uppercase font-medium transition-colors duration-300 ${dark ? "text-gold group-hover:text-hairline" : "text-deep-sea group-hover:text-gold"}`}>
      {cta}
      <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
    </span>
  </Link>
);

export const SubPageCard = HairCard;

export const NumberedColumns = ({ items, dark = true, testId = "hair-columns" }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-10" data-testid={testId}>
    {items.map((item, i) => (
      <Reveal key={item.title} delay={i * 0.1}>
        <div data-testid={`${testId}-${i}`} className="border-t border-gold/60 pt-6">
          <span className="font-serif italic text-gold text-2xl font-light">{i + 1}</span>
          <h3 className={`mt-3 ${dark ? "text-warm-white" : "text-ink"}`}>{item.title}</h3>
          <p className={`text-sm mt-3 leading-relaxed ${dark ? "text-warm-white/75" : "text-ink/75"}`}>{item.text}</p>
        </div>
      </Reveal>
    ))}
  </div>
);

export const HairFaq = ({ faqs, dark = false, testId = "hair-faq" }) => (
  <Accordion type="single" collapsible className="w-full mt-4" data-testid={testId}>
    {faqs.map((faq, i) => (
      <AccordionItem key={i} value={`faq-${i}`} className={dark ? "border-gold/20" : "border-hairline/40"} data-testid={`${testId}-${i}`}>
        <AccordionTrigger className={`faq-q font-normal text-[20px] md:text-[22px] text-left hover:text-gold transition-colors duration-300 py-5 ${dark ? "text-warm-white" : "text-ink"}`}>
          {faq.q}
        </AccordionTrigger>
        <AccordionContent className={`text-sm leading-[1.8] pb-5 ${dark ? "text-warm-white/75" : "text-ink/75"}`}>{faq.a}</AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

export const HairRiskPanel = ({ risks, dark = false }) => (
  <div className={`relative p-8 sm:p-10 ${dark ? "bg-hair-panel" : "bg-cream"}`} data-testid="risk-panel">
    <div className="gold-frame inset-3 opacity-80" aria-hidden="true" />
    <div className="relative">
      <h3 className={dark ? "text-warm-white" : "text-ink"}>Risks and what to expect</h3>
      <div className="mt-5"><Bullets items={risks} dark={dark} /></div>
      <p className={`text-sm mt-6 leading-relaxed ${dark ? "text-warm-white/55" : "text-soft-grey"}`}>
        Any surgical or invasive procedure carries risks. Before proceeding, you should seek a second opinion from an appropriately qualified health practitioner.
      </p>
    </div>
  </div>
);

export const DoctorBlock = ({ dark = false, heading = "Planned and performed by Dr Kristy Truong", testId = "hair-doctor-block" }) => {
  const truong = getTeamMember("truong");
  return (
    <div className="grid grid-cols-1 lg:grid-cols-11 gap-12 items-center" data-testid={testId}>
      <div className="lg:col-span-7">
        <HairEyebrow className="mb-3">Your doctor</HairEyebrow>
        <HairHeading dark={dark}>{heading}</HairHeading>
        <p className={`mt-6 leading-[1.8] max-w-2xl ${dark ? "text-warm-white/80" : "text-ink/80"}`}>
          US fellowship-trained in facial plastic and reconstructive surgery, with further training in hair and brow restoration. The doctor who assesses you is the doctor who designs your hairline, performs your procedure and sees you at every review — in person, in Cottesloe.
        </p>
        <Link to="/hair/dr-kristy-truong" data-testid={`${testId}-link`} className={`inline-flex items-center gap-2 mt-6 text-[12px] tracking-[0.16em] uppercase font-medium transition-colors duration-300 ${dark ? "text-gold hover:text-hairline" : "text-deep-sea hover:text-gold"}`}>
          About Dr Truong <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
        </Link>
      </div>
      <div className="lg:col-span-4">
        <div className={`relative overflow-hidden ${dark ? "ring-1 ring-gold/30" : "ring-1 ring-hairline/60"}`}>
          <img src={truong.photo} alt="Dr Kristy Truong" loading="lazy" decoding="async" className="w-full aspect-[4/5] object-cover" style={{ objectPosition: "50% 18%" }} data-testid={`${testId}-portrait`} />
        </div>
        <p className="eyebrow eyebrow-dark mt-4">{truong.title}</p>
        <p className={`font-serif text-2xl mt-1 ${dark ? "text-warm-white" : "text-ink"}`}>{truong.name}</p>
        <p className={`text-[13px] mt-1 ${dark ? "text-warm-white/65" : "text-ink/65"}`}>{truong.credential} · AHPRA {truong.ahpra}</p>
      </div>
    </div>
  );
};

export const CandidacyBand = () => (
  <HairBand sand narrow testId="hair-candidacy">
    <Reveal>
      <HairEyebrow className="mb-4">Candidacy</HairEyebrow>
      <h2 className="text-ink">{HAIR_LANDING.candidacy.heading}</h2>
      <GoldRule className="mt-5" />
      <p className="text-ink/85 leading-[1.85] mt-6 text-[17px]" data-testid="hair-candidacy-body">{HAIR_LANDING.candidacy.body}</p>
    </Reveal>
  </HairBand>
);

export const PracticeStandards = ({ dark = false }) => (
  <HairBand dark={dark} narrow testId="practice-standards">
    <Reveal>
      <HairEyebrow className="mb-4">Practice standards</HairEyebrow>
      <HairHeading dark={dark}>{PRACTICE_STANDARDS.title}</HairHeading>
      <GoldRule className="mt-5" />
      <p className={`leading-[1.85] mt-6 text-[15px] ${dark ? "text-warm-white/80" : "text-ink/80"}`}>{PRACTICE_STANDARDS.body}</p>
    </Reveal>
  </HairBand>
);

export const PathwayStrip = ({ dark = false }) => (
  <HairBand dark={dark} testId="hair-pathway">
    <Reveal className="mb-12">
      <HairEyebrow className="mb-3">The pathway</HairEyebrow>
      <HairHeading dark={dark}>Six steps, in order</HairHeading>
    </Reveal>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-x-6 gap-y-10">
      {PATHWAY.map((p, i) => (
        <Reveal key={p.step} delay={i * 0.07}>
          <div className="relative pt-6 border-t border-gold/60" data-testid={`pathway-step-${i}`}>
            <span className={`font-serif italic text-gold text-xl absolute -top-4 left-0 pr-3 ${dark ? "bg-hair-dark" : "bg-warm-white"}`}>{i + 1}</span>
            <h3 className={dark ? "text-warm-white" : "text-ink"}>{p.step}</h3>
            <p className={`text-sm mt-2 leading-relaxed ${dark ? "text-warm-white/70" : "text-ink/70"}`}>{p.line}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </HairBand>
);

export const HairCTABand = ({ title = "Book an assessment", text = "Diagnosis first. Then the right treatment — which is not always a transplant." }) => (
  <section className="hair-dark bg-hair-dark relative overflow-hidden" data-testid="hair-cta-band">
    <div className="gold-frame inset-5 sm:inset-7 opacity-70" aria-hidden="true" />
    <div className="max-w-3xl mx-auto px-8 py-24 sm:py-28 text-center relative">
      <Reveal>
        <h2 className="text-warm-white">{title}</h2>
        <p className="text-warm-white/70 mt-5 max-w-xl mx-auto text-base">{text}</p>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <HairBookButton className="btn-hair-gold px-8 py-4 text-sm" source="cta-band" testId="hair-cta-band-button" />
          <ConciergeButton label="Request a callback" practitioner="Dr Kristy Truong" hair testId="hair-cta-band-callback" className="btn-outline-gold inline-flex px-8 py-4 text-sm font-medium tracking-[0.06em]" />
        </div>
      </Reveal>
    </div>
  </section>
);

export const HairCTA = ({ label = "Request a hair assessment", testId = "hair-cta", className = "btn-hair-gold inline-flex px-7 py-3.5 text-sm" }) => (
  <ConciergeButton label={label} practitioner="Dr Kristy Truong" hair testId={testId} className={className} />
);

// Anchor nav pinned beneath the hair header on long single-page layouts.
export const HAIR_ANCHOR_OFFSET = 136;

export const scrollToAnchor = (id, offset = HAIR_ANCHOR_OFFSET) => {
  const el = document.getElementById(id);
  if (!el) return;
  const top = Math.max(0, el.getBoundingClientRect().top + window.scrollY - offset);
  if (window.__lenis) {
    window.__lenis.resize?.();
    window.__lenis.scrollTo(top, { duration: 1.2, force: true });
  } else window.scrollTo({ top, behavior: "smooth" });
};

export const JumpNav = ({ items }) => (
  <nav className="sticky top-[72px] z-30 bg-hair-dark/95 backdrop-blur-md border-y border-gold/15" aria-label="On this page" data-testid="hair-jump-nav">
    <div className="max-w-[1680px] mx-auto px-6 md:px-10 2xl:px-16 flex items-center gap-x-7 gap-y-1 overflow-x-auto no-scrollbar h-12">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => scrollToAnchor(item.id)}
          data-testid={`jump-${item.id}`}
          className={`whitespace-nowrap text-[11px] tracking-[0.18em] uppercase transition-colors duration-300 ${item.id === "book" ? "text-gold hover:text-hairline font-medium" : "text-warm-white/65 hover:text-gold"}`}
        >
          {item.label}
        </button>
      ))}
    </div>
  </nav>
);
